import { Helmet } from 'react-helmet-async';
import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Iconify from '../components/iconify';
import API from '../data/api'; // Import the API module
import CustomTextField from 'src/components/custom/CustomTextField';
import CustomModal from 'src/components/custom/CustomModal';
import CustomPdfViewer from 'src/components/custom/CustomPdfViewer';
import { filePreviewStyle, MAX_FILE_SIZE_MB } from 'src/utils/constants';
import FileDragzone from 'src/components/ui/FileDragzone';
import { LoadingButton } from '@mui/lab';
import { downloadFile } from 'src/utils/fileUtils';
import CustomTablePagination from 'src/components/custom/CustomTablePagination';
import DocumentViewer from 'src/components/ui/DocumentViewer';
import {
  Assessment,
  CheckCircle,
  ErrorOutline,
  HourglassBottom,
  WarningAmber,
  Description,
  ReceiptLong,
  SwapHoriz,
  Gavel,
  Person,
  Schedule,
  Label,
  Category,
} from '@mui/icons-material';

// eslint-disable-next-line no-unused-vars
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

export default function ReportsPage() {
  const [documents, setDocuments] = useState([]);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tagSelections, setTagSelections] = useState({});
  const [tagOptions, setTagOptions] = useState([]);
  const [tagLabelByKey, setTagLabelByKey] = useState({});
  const [openMetadataModal, setOpenMetadataModal] = useState(false);
  const [selectedMetadataDocument, setSelectedMetadataDocument] = useState(null);
  const [openSummaryModal, setOpenSummaryModal] = useState(false);
  const [selectedSummaryDocument, setSelectedSummaryDocument] = useState(null);
  const [metadataDraft, setMetadataDraft] = useState({
    document_type: '',
    department: '',
    visibility_roles: '',
    doc_category: '',
    tags: [],
  });
  const [typeTab, setTypeTab] = useState('ALL');
  const [reportCards, setReportCards] = useState(null);

  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [fileObjects, setFileObjects] = useState([]);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [isFilePreview, setIsFilePreview] = useState(false);
  const [uploadModalStyle, setUploadModalStyle] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const getDocumentKey = (document, fallback) =>
    document?.report_id || document?.id || document?.name || document?.url || fallback;

  const getDocumentName = (document) =>
    document?.name || document?.document_file_name || 'Document';

  const formatDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleString();
  };

  const parseTags = useCallback((tags) => {
    if (Array.isArray(tags)) {
      return tags.filter(Boolean);
    }
    if (typeof tags === 'string') {
      return tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
    return [];
  }, []);

  const normalizeTagKeys = useCallback((tags) => {
    const values = parseTags(tags);
    return values.map((value) => {
      if (value.includes(':')) {
        return value;
      }
      const byLabel = Object.keys(tagLabelByKey).find(
        (key) => tagLabelByKey[key]?.toLowerCase() === value.toLowerCase(),
      );
      return byLabel || value;
    });
  }, [parseTags, tagLabelByKey]);

  const getTagLabel = (key) => tagLabelByKey[key] || key;

  const getLogisticsFlag = () => {
    try {
      const stored = localStorage.getItem('user_info');
      const parsed = stored ? JSON.parse(stored) : null;
      return Boolean(parsed?.logistics);
    } catch (error) {
      console.error('Error reading user_info from storage:', error);
      return false;
    }
  };

  const fetchDocuments = useCallback(async () => {
    try {
      const logisticsFlag = getLogisticsFlag();
      const response = await API.get(
        `sharedfiles/view_all?page=${page}&records=${rowsPerPage}&logistics=${logisticsFlag}${searchTerm ? `&search_term=${searchTerm}` : ''}`,
      );
      const items = response.data.items || response.data.files || [];
      const total = response.data.total || response.data.total_files || 0;
      setDocuments(items);
      setTotalPages(Math.ceil(total / rowsPerPage));
      setTagSelections((prev) => {
        const next = { ...prev };
        items.forEach((doc) => {
          const key = getDocumentKey(doc, doc.name);
          if (!next[key]) {
            next[key] = normalizeTagKeys(doc?.tags);
          }
        });
        return next;
      });
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  }, [normalizeTagKeys, page, rowsPerPage, searchTerm]);

  const fetchTagGroups = useCallback(async () => {
    try {
      const response = await API.get('sharedfiles/tags');
      const options = [];
      const labelByKey = {};
      const tags = response.data || [];
      tags.forEach((tag) => {
        const key = tag?.tag_key || tag?.key || tag?.value;
        const label = tag?.label || tag?.name || key;
        const group = tag?.tag_group || 'GENERAL';
        if (!key) {
          return;
        }
        options.push({ key, label, group });
        labelByKey[key] = label;
      });
      setTagOptions(options);
      setTagLabelByKey(labelByKey);
    } catch (error) {
      console.error('Error fetching tag groups:', error);
    }
  }, []);

  const updateDocumentTags = async (document, tagKeys) => {
    const reportId = document?.report_id;
    if (!reportId) {
      return;
    }
    try {
      await API.put(`sharedfiles/${reportId}/tags/by-key`, {
        tag_keys: tagKeys,
      });
      const key = getDocumentKey(document, document.name);
      setDocuments((prev) =>
        prev.map((doc) =>
          getDocumentKey(doc, doc.name) === key ? { ...doc, tags: tagKeys } : doc,
        ),
      );
    } catch (error) {
      console.error('Error updating report tags:', error);
    }
  };

  const fetchReportCards = useCallback(async () => {
    try {
      const response = await API.get('sharedfiles/reports/cards');
      setReportCards(response.data?.cards || null);
    } catch (error) {
      console.error('Error fetching report cards:', error);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    fetchReportCards();
  }, [fetchReportCards]);

  useEffect(() => {
    fetchTagGroups();
  }, [fetchTagGroups]);

  const handleView = (document) => {
    setSelectedDocument(document);
    setOpenViewModal(true);
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setSelectedDocument(null);
  };

  const handleOpenSummaryModal = (document) => {
    setSelectedSummaryDocument(document);
    setOpenSummaryModal(true);
  };

  const handleCloseSummaryModal = () => {
    setOpenSummaryModal(false);
    setSelectedSummaryDocument(null);
  };

  const handleOpenMetadataModal = (document) => {
    setSelectedMetadataDocument(document);
    setMetadataDraft({
      document_type: document?.document_type || '',
      department: document?.department || '',
      visibility_roles: Array.isArray(document?.visibility_roles)
        ? document.visibility_roles.join(', ')
        : document?.visibility_roles || '',
      doc_category: document?.doc_category || '',
      tags: normalizeTagKeys(document?.tags),
    });
    setOpenMetadataModal(true);
  };

  const handleCloseMetadataModal = () => {
    setOpenMetadataModal(false);
    setSelectedMetadataDocument(null);
  };

  const handleSaveMetadata = async () => {
    if (!selectedMetadataDocument) {
      return;
    }
    await updateDocumentTags(selectedMetadataDocument, metadataDraft.tags);
    const key = getDocumentKey(selectedMetadataDocument);
    setTagSelections((prev) => ({ ...prev, [key]: metadataDraft.tags }));
    handleCloseMetadataModal();
  };

  const handleFileObjects = (files) => {
    const file = (files.length > 0) ? files[0] : null;
    setSelectedFile(file);
    setFileObjects(files);
    setFilePreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  const handleUploadDocumentPreview = () => {

    if (fileObjects.length === 0 || fileObjects[0] === null) {
      return;
    }

    console.log('uploadDocumentPreview ', fileObjects[0]);
    setUploadModalStyle({ ...filePreviewStyle });
    setIsFilePreview(true);
  }

  const handleUpload = () => {
    setOpenUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setOpenUploadModal(false);
    setSelectedFile(null);
    setUploadModalStyle({ });
    setIsFilePreview(false);
    setFileObjects([]);
    setFilePreviewUrl(null);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;
  
    const formData = new FormData();
    formData.append('file', selectedFile);
    setSubmitted(true);

    try {
      // Adjust the API call based on your backend URL
      await API.put('sharedfiles/upload?service_provider=aws', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      handleCloseUploadModal();
      fetchDocuments(); // Refresh the document list after upload
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleDelete = async (fileName) => {
    try {
      await API.delete(`sharedfiles/delete_single_file/aws/${fileName}`);
      fetchDocuments(); // Refresh the document list after deletion
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handlePageChange = (direction) => {
    let currentPage = page;
    if (direction === 'next') {
      currentPage = Math.min(currentPage + 1, totalPages);
    } else {
      currentPage = Math.max(currentPage - 1, 1);
    }
    setPage(currentPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const records = parseInt(event.target.value, 10);
    setRowsPerPage(records);
    setPage(1);
  }

  const tagOptionsList = React.useMemo(() => tagOptions, [tagOptions]);

  const documentTypes = React.useMemo(() => {
    const types = new Set(['ALL']);
    documents.forEach((doc) => {
      const type = doc?.document_type || 'UNKNOWN';
      types.add(type);
    });
    return Array.from(types);
  }, [documents]);

  const visibleDocuments = React.useMemo(() => {
    if (typeTab === 'ALL') {
      return documents;
    }
    return documents.filter(
      (doc) => (doc?.document_type || 'UNKNOWN') === typeTab,
    );
  }, [documents, typeTab]);

  const reportCardConfig = [
    { key: 'total_suborders_tracked', label: 'Total Tracked', icon: Assessment, accent: '#0F766E' },
    { key: 'suborders_complete', label: 'Complete', icon: CheckCircle, accent: '#166534' },
    { key: 'suborders_incomplete', label: 'Incomplete', icon: ErrorOutline, accent: '#991B1B' },
    { key: 'suborders_nearly_complete', label: 'Nearly Complete', icon: HourglassBottom, accent: '#A16207' },
    { key: 'suborders_critical_missing', label: 'Critical Missing', icon: WarningAmber, accent: '#B91C1C' },
    { key: 'missing_release', label: 'Missing Release', icon: Description, accent: '#0E7490' },
    { key: 'missing_commercial_invoice', label: 'Missing Invoice', icon: ReceiptLong, accent: '#7C2D12' },
    { key: 'missing_transit_clearance', label: 'Missing Transit', icon: SwapHoriz, accent: '#1D4ED8' },
    { key: 'missing_sars_customs', label: 'Missing SARS', icon: Gavel, accent: '#7C3AED' },
    { key: 'missing_burs_docs', label: 'Missing BURS', icon: Gavel, accent: '#4D7C0F' },
  ];

  return (
    <>
      <Helmet>
        <title>Reports</title>
      </Helmet>

      <Container maxWidth="false">
        <Box
          sx={{ display: 'flex', borderRadius: 1, }}
        >
          <Box sx={{ width: '100%' }}>
            <Typography variant="h4">
              Reports
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexShrink: 0, mr: 2 }}>
            <Button  variant='contained' sx={{ mb: 2 }} onClick={handleUpload}>
              Upload Document
            </Button>
          </Box>
        </Box>
        <Box sx={{ mt: 2, mb: 3 }}>
          <Grid container spacing={2}>
            {reportCardConfig.map((card) => {
              const Icon = card.icon;
              return (
              <Grid key={`report-card-${card.key}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, rgba(249,250,251,1) 0%, rgba(255,255,255,1) 60%)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 6,
                      backgroundColor: card.accent,
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 1.5,
                        backgroundColor: `${card.accent}1A`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon sx={{ color: card.accent }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {card.label}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: 'text.primary' }}>
                        {reportCards?.[card.key] ?? 0}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
            })}
          </Grid>
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <CustomTextField
            id="search"
            label="Search Documents"
            variant="outlined"
            onChange={handleSearchChange}
          />
        </FormControl>

        <Tabs
          value={typeTab}
          onChange={(_, value) => {
            setTypeTab(value);
            setPage(1);
          }}
          sx={{ mt: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {documentTypes.map((type) => (
            <Tab key={`type-${type}`} label={type} value={type} />
          ))}
        </Tabs>

        <TableContainer sx={{ mt: 2, width: '100%', overflowX: { xs: 'scroll', sm: 'auto' } }} >
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Suborder</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Uploaded By</TableCell>
                <TableCell>Uploaded At</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleDocuments.map((document) => (
                <TableRow key={getDocumentKey(document, document.name)}>
                  <TableCell component="th" scope="row">
                    <Iconify icon={'ion:documents-sharp'} sx={{ fontSize: '36px', verticalAlign: 'middle', mr: 2 }} />
                    {getDocumentName(document)}
                  </TableCell>
                  <TableCell>{document.suborder_id ?? '-'}</TableCell>
                  <TableCell>{document.document_type || '-'}</TableCell>
                  <TableCell>{document.uploaded_by || '-'}</TableCell>
                  <TableCell>{formatDateTime(document.uploaded_at)}</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={tagOptionsList}
                      getOptionLabel={(option) =>
                        typeof option === 'string' ? getTagLabel(option) : option.label
                      }
                      isOptionEqualToValue={(option, value) =>
                        (option.key || option) === (value.key || value)
                      }
                      value={
                        (tagSelections[getDocumentKey(document, document.name)] || [])
                          .map((key) => tagOptionsList.find((opt) => opt.key === key) || key)
                      }
                      onChange={async (_, value) => {
                        const key = getDocumentKey(document, document.name);
                        const keys = value.map((item) => item.key || item);
                        setTagSelections((prev) => ({ ...prev, [key]: keys }));
                        await updateDocumentTags(document, keys);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            size="small"
                            label={typeof option === 'string' ? getTagLabel(option) : option.label}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Tags" />
                      )}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Summary">
                      <IconButton
                        color="default"
                        onClick={() => handleOpenSummaryModal(document)}
                      >
                        <Iconify icon={'mdi:information-outline'} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Metadata">
                      <IconButton
                        color="info"
                        onClick={() => handleOpenMetadataModal(document)}
                      >
                        <Iconify icon={'mdi:file-document-edit-outline'} />
                      </IconButton>
                    </Tooltip>
                    <IconButton color="primary" onClick={() => handleView(document)}>
                      <Iconify icon={'mdi:eye'} />
                    </IconButton>
                    <IconButton color="secondary" onClick={() => downloadFile(document?.url, document?.name)}>
                      <Iconify icon={'mdi:download'} />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(document.name)}>
                      <Iconify icon={'mdi:delete'} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <CustomTablePagination
          rowsPerPage={rowsPerPage}
          recordsList={[5, 10, 15, 20]}
          handleChangeRowsPerPage={(e) =>handleChangeRowsPerPage(e)}
          handleNextPage={handlePageChange}
          currentPage={page}
          totalPages={totalPages}
        />

        <DocumentViewer
          documentUrl={selectedDocument?.url}
          openModal={openViewModal}
          handleCloseModal={handleCloseViewModal}
        />

        <CustomModal
          openModal={openMetadataModal}
          handleCloseModal={handleCloseMetadataModal}
          modalTitle={`Edit Metadata ${selectedMetadataDocument?.name ? `- ${selectedMetadataDocument.name}` : ''}`}
          modalIcon="document"
        >
          <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
            <TextField
              label="Document Type"
              value={metadataDraft.document_type}
              onChange={(event) =>
                setMetadataDraft((prev) => ({
                  ...prev,
                  document_type: event.target.value,
                }))
              }
            />
            <TextField
              label="Department"
              value={metadataDraft.department}
              onChange={(event) =>
                setMetadataDraft((prev) => ({
                  ...prev,
                  department: event.target.value,
                }))
              }
            />
            <TextField
              label="Document Category"
              value={metadataDraft.doc_category}
              onChange={(event) =>
                setMetadataDraft((prev) => ({
                  ...prev,
                  doc_category: event.target.value,
                }))
              }
            />
            <TextField
              label="Visibility Roles (comma-separated)"
              value={metadataDraft.visibility_roles}
              onChange={(event) =>
                setMetadataDraft((prev) => ({
                  ...prev,
                  visibility_roles: event.target.value,
                }))
              }
            />
            <Autocomplete
              multiple
              options={tagOptionsList}
              getOptionLabel={(option) =>
                typeof option === 'string' ? getTagLabel(option) : option.label
              }
              isOptionEqualToValue={(option, value) =>
                (option.key || option) === (value.key || value)
              }
              value={metadataDraft.tags.map((key) => tagOptionsList.find((opt) => opt.key === key) || key)}
              onChange={(_, value) =>
                setMetadataDraft((prev) => ({
                  ...prev,
                  tags: value.map((item) => item.key || item),
                }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    size="small"
                    label={typeof option === 'string' ? getTagLabel(option) : option.label}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Tags" />
              )}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'right', mt: 3 }}>
            <Button variant="contained" color="inherit" onClick={handleCloseMetadataModal}>
              Cancel
            </Button>
            <LoadingButton sx={{ ml: 2 }} variant="contained" onClick={handleSaveMetadata}>
              Save
            </LoadingButton>
          </Box>
        </CustomModal>

        <CustomModal
          openModal={openSummaryModal}
          handleCloseModal={handleCloseSummaryModal}
          modalTitle={`Document Summary${selectedSummaryDocument?.name ? ` - ${selectedSummaryDocument.name}` : ''}`}
          modalIcon="document"
        >
          <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
            <Box
              sx={{
                borderLeft: '4px solid #2563EB',
                pl: 2,
                display: 'flex',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  backgroundColor: '#DBEAFE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Person sx={{ color: '#2563EB' }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Uploaded By
                </Typography>
                <Typography variant="body1">
                  {selectedSummaryDocument?.uploaded_by || '-'}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                borderLeft: '4px solid #0EA5E9',
                pl: 2,
                display: 'flex',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  backgroundColor: '#E0F2FE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Schedule sx={{ color: '#0EA5E9' }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Uploaded At
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedSummaryDocument?.uploaded_at)}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                borderLeft: '4px solid #7C3AED',
                pl: 2,
                display: 'flex',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  backgroundColor: '#EDE9FE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Description sx={{ color: '#7C3AED' }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Document Type
                </Typography>
                <Typography variant="body1">
                  {selectedSummaryDocument?.document_type || '-'}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                borderLeft: '4px solid #16A34A',
                pl: 2,
                display: 'flex',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  backgroundColor: '#DCFCE7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Category sx={{ color: '#16A34A' }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Category
                </Typography>
                <Typography variant="body1">
                  {selectedSummaryDocument?.doc_category || '-'}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                borderLeft: '4px solid #F97316',
                pl: 2,
                display: 'flex',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  backgroundColor: '#FFEDD5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Label sx={{ color: '#F97316' }} />
              </Box>
              <Box sx={{ width: '100%' }}>
                <Typography variant="caption" color="text.secondary">
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {normalizeTagKeys(selectedSummaryDocument?.tags).length > 0
                    ? normalizeTagKeys(selectedSummaryDocument?.tags).map((tag) => (
                      <Chip key={`summary-tag-${tag}`} label={getTagLabel(tag)} size="small" />
                    ))
                    : '-'}
                </Box>
              </Box>
            </Box>
          </Box>
        </CustomModal>

        <CustomModal
          openModal={openUploadModal}
          handleCloseModal={handleCloseUploadModal}
          modalTitle={`Upload ${selectedFile?.name || 'Document'}`}
          modalIcon='document'
          modalStyle={uploadModalStyle}
          clearSign={!submitted}
        >
          {(!isFilePreview) && (
            <>
              <Box sx={{ my: 2 }}>
                <FileDragzone
                  acceptedTypes={{
                    'application/pdf': ['.pdf'],
                  }}
                  fileObjects={fileObjects}
                  handleFileObjects={handleFileObjects}
                  filesLimit={1}
                  maxFileSizeMB={MAX_FILE_SIZE_MB}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'right', mt: 2 }}>
                <Button
                  variant='contained'
                  color="inherit"
                  onClick={handleCloseUploadModal}
                >
                  Cancel
                </Button>
                <LoadingButton
                  sx={{ ml: 2, }}
                  variant="contained"
                  onClick={(handleUploadDocumentPreview)}
                >
                  Continue
                </LoadingButton>
              </Box>
            </>
          )}

          {isFilePreview && (
            <>
              <Box sx={{ mt: 2 }}>
                <Grid item size={{ xs: 12 }} sx={{ my: 'auto', display: 'flex', justifyContent: 'center' }}>
                  <CustomPdfViewer
                    fileUrl={filePreviewUrl}
                  />
                </Grid>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'right', mt: 2 }}>
                <Button
                  variant='contained'
                  color="inherit"
                  disabled={submitted}
                  onClick={handleCloseUploadModal}
                >
                  Cancel
                </Button>
                <LoadingButton
                  sx={{ ml: 2, }}
                  variant="contained"
                  loading={submitted}
                  onClick={handleFileUpload}
                >
                  Finish
                </LoadingButton>
              </Box>
            </>
          )}
        </CustomModal>
      </Container>
    </>
  );
}
